import React, { useEffect, useRef, useState } from "react";
import Hls from "hls.js";
import { usePlayer } from "../context/PlayerContext";

/**
 * Background video layer that streams GFN Soul NYC TV muted + looping behind
 * the hero banner. Sits below the banner image and clickable hotspots.
 *
 * - Auto-mutes so browsers allow autoplay
 * - Uses the same HLS URL as the TV station (env: REACT_APP_STREAM_TV)
 * - Pauses itself when the persistent TV window is active to save bandwidth
 * - Falls back gracefully (stays hidden) when HLS can't play
 */
const TV_SRC = process.env.REACT_APP_STREAM_TV;

const HeroVideoBackground = () => {
  const videoRef = useRef(null);
  const hlsRef = useRef(null);
  const [ready, setReady] = useState(false);
  const { current } = usePlayer();
  const tvAlreadyPlaying = current?.kind === "video";

  useEffect(() => {
    const el = videoRef.current;
    if (!el || !TV_SRC) return;

    // Don't attach if the persistent TV window is already streaming — avoids
    // duplicating bandwidth.
    if (tvAlreadyPlaying) {
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
      el.pause();
      return;
    }

    let hls;
    const onCanPlay = () => setReady(true);
    el.addEventListener("canplay", onCanPlay);

    if (Hls.isSupported()) {
      hls = new Hls({ enableWorker: true, lowLatencyMode: false });
      hls.loadSource(TV_SRC);
      hls.attachMedia(el);
      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        el.play().catch(() => {});
      });
      hls.on(Hls.Events.ERROR, (_evt, data) => {
        if (data.fatal) {
          // Silently disable — banner still shows underneath
          hls.destroy();
          hlsRef.current = null;
        }
      });
      hlsRef.current = hls;
    } else if (el.canPlayType("application/vnd.apple.mpegurl")) {
      el.src = TV_SRC;
      el.play().catch(() => {});
    }

    return () => {
      el.removeEventListener("canplay", onCanPlay);
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
    };
  }, [tvAlreadyPlaying]);

  if (!TV_SRC) return null;

  return (
    <div
      data-testid="hero-video-bg"
      aria-hidden
      className="absolute inset-0 overflow-hidden pointer-events-none"
      style={{ zIndex: 0 }}
    >
      <video
        ref={videoRef}
        muted
        autoPlay
        loop
        playsInline
        preload="metadata"
        className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${
          ready ? "opacity-40" : "opacity-0"
        }`}
      />
      {/* Dark vignette + gold tint so the banner stays crisp on top */}
      <div
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse at center, rgba(0,0,0,0.25) 0%, rgba(0,0,0,0.85) 75%), linear-gradient(180deg, rgba(0,0,0,0.4) 0%, rgba(0,0,0,0.9) 100%)",
        }}
      />
    </div>
  );
};

export default HeroVideoBackground;
